import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { AppConfig } from "../AppConfig";
import { getMapView } from "./arcgisUtils";
import { NFSStatus } from "./nfsUtils";
import { FeatureClasses, NauticalClasses } from "../constants/NauticalObjects";
import { SeamarkColors } from "../constants/nfsDomains";
import type Point from "@arcgis/core/geometry/Point";
import type Graphic from "@arcgis/core/Graphic";
import type FeatureSet from "@arcgis/core/rest/support/FeatureSet";

// Cache for existing seamark numbers to avoid redundant API calls
interface SeamarkNumberCache {
    data: string[];
    timestamp: number;
    region: number;
}

let seamarkNumberCache: SeamarkNumberCache | null = null;
const CACHE_DURATION_MS = 30000; // 30 seconds cache duration

/**
 * Clear the seamark number cache. Call this when seamark numbers are modified.
 */
export const clearSeamarkNumberCache = (): void => {
    seamarkNumberCache = null;
};

enum KYVRegions {
    "Udefinert" = 0,
    "KystverketSørøst" = 1,
    "KystverketVest" = 2,
    "KystverketMidtNorge" = 3,
    "KystverketNordland" = 4,
    "KystverketTromsOgFinnmark" = 5,
    "KystverketSvalbard" = 6,
    "Kontinentalsokkel" = 7,
}

enum NumberingRule {
    "Hundreds" = 1,
    "Tens" = 2,
    "Even" = 3,
    "Odd" = 4,
    "NoLight" = 5,
}

export const generateNauticalObjectSeamarkNumber = async (nauticalObject: Graphic): Promise<string> => {
    let seamarkNumber = "000000";

    if (!nauticalObject?.attributes) {
        return seamarkNumber;
    }

    const objectHasLight = Object.hasOwn(nauticalObject.attributes, "lys_type");

    const region = nauticalObject.attributes.kystverk_region;

    const { lowValue, highValue, message } = getValidSeamarkRange(nauticalObject);

    if (message !== "") {
        return seamarkNumber; // Not enough information to generate a seamark number
    }

    let existingSeamarkNumbers = await getExistingSeamarkNumbers(nauticalObject, region);

    // Objects with lights ends in number, objects without lights ends with M - Some exceptions exists, but are not defined in the rules
    if (objectHasLight) {
        const filteredSeamarkNumbers = existingSeamarkNumbers.filter((num) => !num.endsWith("M"));
        filteredSeamarkNumbers.sort((a, b) => Number.parseInt(a, 10) - Number.parseInt(b, 10));
        existingSeamarkNumbers = filteredSeamarkNumbers;
    } else {
        const filteredSeamarkNumbers = existingSeamarkNumbers.filter((num) => num.endsWith("M"));
        filteredSeamarkNumbers.sort(
            (a, b) => Number.parseInt(a.slice(0, -1), 10) - Number.parseInt(b.slice(0, -1), 10)
        );
        existingSeamarkNumbers = filteredSeamarkNumbers;
    }

    let numberingRule: NumberingRule | undefined = undefined;

    switch (nauticalObject.attributes.klasse) {
        case NauticalClasses.FL_Fyrlykt:
        case NauticalClasses.FS_Fyrstasjon:
            numberingRule = NumberingRule.Hundreds;
            break;

        case NauticalClasses.IB_IndirekteBelysning:
            numberingRule = NumberingRule.Tens;
            break;
        case NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning:
        case NauticalClasses.LA_Lanterne:
        case NauticalClasses.LB_Lysboeye:
            if (nauticalObject.attributes.lys_farge === SeamarkColors.Red) {
                numberingRule = NumberingRule.Even;
            } else if (nauticalObject.attributes.lys_farge === SeamarkColors.Green) {
                numberingRule = NumberingRule.Odd;
            } else if (
                nauticalObject.attributes.lys_farge === SeamarkColors.White ||
                nauticalObject.attributes.lys_farge === SeamarkColors.Yellow
            ) {
                numberingRule = NumberingRule.Tens;
            } else {
                return seamarkNumber; // Not enough information to generate a seamark number
            }
            break;
    }

    if (!objectHasLight) {
        numberingRule = NumberingRule.NoLight;
    }

    switch (numberingRule) {
        case NumberingRule.Hundreds:
            seamarkNumber = generateHundredsSeamarkNumber(existingSeamarkNumbers, lowValue, highValue);
            break;
        case NumberingRule.Tens:
            seamarkNumber = generateTensSeamarkNumber(existingSeamarkNumbers, lowValue, highValue);
            break;
        case NumberingRule.Even:
            seamarkNumber = generateEvenSeamarkNumber(existingSeamarkNumbers, lowValue, highValue);
            break;
        case NumberingRule.Odd:
            seamarkNumber = generateOddSeamarkNumber(existingSeamarkNumbers, lowValue, highValue);
            break;
        case NumberingRule.NoLight:
            seamarkNumber = generateNoLightSeamarkNumber(existingSeamarkNumbers, lowValue, highValue);
            break;
        default:
            return seamarkNumber; // Not enough information to generate a seamark number
    }

    if (seamarkNumber === "") {
        seamarkNumber = "000000"; // Fallback if no number could be generated
    }
    return seamarkNumber;
};

const getExistingSeamarkNumbers = async (nauticalObject: Graphic, region: number): Promise<string[]> => {
    const seamarkNumbers: string[] = [];

    if (!nauticalObject?.attributes) {
        return seamarkNumbers;
    }

    // Check if we have valid cached data for this region
    const now = Date.now();
    if (
        seamarkNumberCache &&
        seamarkNumberCache.region === region &&
        now - seamarkNumberCache.timestamp < CACHE_DURATION_MS
    ) {
        // Filter out the current object's seamark number if it exists in cache
        return seamarkNumberCache.data.filter((num) => nauticalObject.attributes.sjomerke_nr !== num);
    }

    const layer = nauticalObject.layer as FeatureLayer;
    if (!layer) {
        return seamarkNumbers;
    }

    let whereClause = "";

    switch (region) {
        case KYVRegions.KystverketSørøst:
            whereClause = "nfs_status NOT IN (21, 23) AND ((sjomerke_nr >= '000100' AND sjomerke_nr <= '100000'))";
            break;
        case KYVRegions.KystverketVest:
            whereClause = "nfs_status NOT IN (21, 23) AND ((sjomerke_nr >= '091300' AND sjomerke_nr <= '300000'))";
            break;
        case KYVRegions.KystverketMidtNorge:
            whereClause = "nfs_status NOT IN (21, 23) AND ((sjomerke_nr >= '300000' AND sjomerke_nr <= '600000'))";
            break;
        case KYVRegions.KystverketNordland:
            whereClause = "nfs_status NOT IN (21, 23) AND ((sjomerke_nr >= '568000' AND sjomerke_nr <= '850000'))";
            break;
        case KYVRegions.KystverketTromsOgFinnmark:
            whereClause = "nfs_status NOT IN (21, 23) AND ((sjomerke_nr >= '835100' AND sjomerke_nr <= '999999'))";
            break;
        case KYVRegions.KystverketSvalbard:
            whereClause = "nfs_status NOT IN (21, 23) AND ((sjomerke_nr >= '986000' AND sjomerke_nr <= '999999'))";
            break;
        case KYVRegions.Kontinentalsokkel:
        case KYVRegions.Udefinert:
        default:
            whereClause = "1=2";
            break;
    }

    if (whereClause === "1=2") {
        return seamarkNumbers; // No valid seamark numbers for this region
    }

    const mapView = getMapView();
    const allLayers = mapView?.map?.allLayers?.toArray() || [];

    // Find all feature layers that have the sjomerke_nr field
    const layersWithSeamarkNumberAndStatus = allLayers.filter((layer) => {
        return (
            layer instanceof FeatureLayer &&
            layer.fields?.some((field) => field.name === "sjomerke_nr") &&
            layer.fields?.some((field) => field.name === "nfs_status")
        );
    }) as FeatureLayer[];

    // Add the current layer if it's not already included
    if (
        !layersWithSeamarkNumberAndStatus.includes(layer) &&
        layer.fields?.some((field) => field.name === "sjomerke_nr") &&
        layer.fields?.some((field) => field.name === "nfs_status")
    ) {
        layersWithSeamarkNumberAndStatus.push(layer);
    }

    let allFeatures: FeatureSet["features"] = [];

    // Helper function to query a single layer
    const queryLayer = async (featureLayer: FeatureLayer): Promise<FeatureSet["features"]> => {
        const layerFeatures: FeatureSet["features"] = [];

        try {
            let additionalWhere = "";
            if (featureLayer.id === "IB") {
                additionalWhere = " AND tilknyttet_HIB <> 1 AND tilknyttet_fl_la <> 1 "; // Only IB not linked to HIB or FL/LA
            }
            if (featureLayer.id === "Flytende merke") {
                additionalWhere = " AND tilknyttet_lysboye <> 1"; // Only Flytende merke not linked to lysbøye
            }

            const baseWhere =
                whereClause +
                additionalWhere +
                (nauticalObject.attributes.objectid ? " AND objectid <> " + nauticalObject.attributes.objectid : "");

            // First check the count to know if we need to batch the queries
            const featureCount = await featureLayer.queryFeatureCount({
                where: baseWhere,
            });

            if (featureCount === 0) {
                return layerFeatures;
            }

            // If count is manageable, do a single query
            if (featureCount <= 1000) {
                const queryResults = await featureLayer.queryFeatures({
                    where: baseWhere,
                    outFields: ["sjomerke_nr"],
                    returnGeometry: false,
                });

                if (queryResults?.features?.length > 0) {
                    layerFeatures.push(...queryResults.features);
                }
            } else {
                // Use batching for large result sets to prevent browser freezing
                const batchSize = 2000;
                const totalBatches = Math.ceil(featureCount / batchSize);
                const maxConcurrentQueries = 8;

                // Process batches in chunks of maxConcurrentQueries
                for (let i = 0; i < totalBatches; i += maxConcurrentQueries) {
                    const batchPromises = [];

                    // Create a batch of promises (up to maxConcurrentQueries)
                    for (let j = 0; j < maxConcurrentQueries && i + j < totalBatches; j++) {
                        const batchIndex = i + j;
                        const queryPromise = featureLayer.queryFeatures({
                            where: baseWhere,
                            outFields: ["sjomerke_nr"],
                            returnGeometry: false,
                            num: batchSize,
                            start: batchIndex * batchSize,
                        });
                        batchPromises.push(queryPromise);
                    }

                    // Wait for all concurrent batch queries to complete
                    const batchResults = await Promise.all(batchPromises);

                    // Process the results of each batch
                    for (const queryResults of batchResults) {
                        if (queryResults?.features?.length > 0) {
                            layerFeatures.push(...queryResults.features);
                        }
                    }
                }
            }
        } catch (error) {
            console.error(`Error querying layer ${featureLayer.id}:`, error);
        }

        return layerFeatures;
    };

    // Query all layers in parallel
    const layerQueryPromises = layersWithSeamarkNumberAndStatus.map((layer) => queryLayer(layer));
    const layerResults = await Promise.all(layerQueryPromises);

    // Combine all features from all layers
    for (const features of layerResults) {
        allFeatures.push(...features);
    }

    const results = { features: allFeatures };

    if (results?.features && results.features.length > 0) {
        results.features.forEach((feature) => {
            const seamarkNumber = feature.attributes.sjomerke_nr;
            if (seamarkNumber) {
                seamarkNumbers.push(seamarkNumber);
            }
        });
    }

    // Cache the results
    seamarkNumberCache = {
        data: seamarkNumbers,
        timestamp: Date.now(),
        region: region,
    };

    return seamarkNumbers;
};

const generateNoLightSeamarkNumber = (
    existingSeamarkNumbers: string[],
    lowValue: string,
    highValue: string
): string => {
    const existingNumbers = new Set(existingSeamarkNumbers.map((num) => Number.parseInt(num)));
    const low = Number.parseInt(lowValue);
    const high = Number.parseInt(highValue);

    // Both existing numbers and low/high have had the last digit removed since they end with M
    for (let i = low; i <= high; i++) {
        if (!existingNumbers.has(i)) {
            if (i > high) {
                return ""; // No available seamark number found
            } else {
                return i.toString().concat("M").padStart(6, "0");
            }
        }
    }

    return ""; // No available seamark number found
};

const generateHundredsSeamarkNumber = (
    existingSeamarkNumbers: string[],
    lowValue: string,
    highValue: string
): string => {
    const existingNumbers = new Set(existingSeamarkNumbers.map((num) => Number.parseInt(num, 10)));
    const low = Number.parseInt(lowValue, 10);
    const high = Number.parseInt(highValue, 10);

    for (let i = low; i <= high; i += 100) {
        if (!existingNumbers.has(i)) {
            return i.toString().padStart(6, "0");
        }
    }

    return ""; // No available seamark number found
};

const generateTensSeamarkNumber = (existingSeamarkNumbers: string[], lowValue: string, highValue: string): string => {
    const existingNumbers = new Set(existingSeamarkNumbers.map((num) => Number.parseInt(num, 10)));
    const low = Number.parseInt(lowValue, 10);
    const high = Number.parseInt(highValue, 10);

    for (let i = low; i <= high; i += 10) {
        if (i % 100 !== 0 && !existingNumbers.has(i)) {
            return i.toString().padStart(6, "0");
        }
    }

    return ""; // No available seamark number found
};

const generateEvenSeamarkNumber = (existingSeamarkNumbers: string[], lowValue: string, highValue: string): string => {
    const existingNumbers = new Set(existingSeamarkNumbers.map((num) => Number.parseInt(num, 10)));
    const low = Number.parseInt(lowValue, 10);
    const high = Number.parseInt(highValue, 10);

    for (let i = low; i <= high; i++) {
        if (i % 2 === 0 && !existingNumbers.has(i)) {
            return i.toString().padStart(6, "0");
        }
    }

    return ""; // No available seamark number found
};

const generateOddSeamarkNumber = (existingSeamarkNumbers: string[], lowValue: string, highValue: string): string => {
    const existingNumbers = new Set(existingSeamarkNumbers.map((num) => Number.parseInt(num, 10)));
    const low = Number.parseInt(lowValue, 10);
    const high = Number.parseInt(highValue, 10);

    for (let i = low; i <= high; i++) {
        if (i % 2 !== 0 && !existingNumbers.has(i)) {
            return i.toString().padStart(6, "0");
        }
    }

    return ""; // No available seamark number found
};

export const validateSeamarkNumber = async (value: string, nauticalObject: Graphic): Promise<string> => {
    if (!value || value.length !== 6) {
        return "Sjømerke nummer må være 6 sifre langt.";
    }

    const isRacon = (nauticalObject.layer as FeatureLayer).layerId === FeatureClasses.Racon;

    if (nauticalObject.attributes.hasOwnProperty("nfs_status")) {
        if (nauticalObject.attributes.nfs_status !== NFSStatus.PROJECTED) {
            return "";
        }
    }

    const objectHasLight = Object.hasOwn(nauticalObject.attributes, "lys_type");

    const region = nauticalObject.attributes.kystverk_region;
    const { message } = getValidSeamarkRange(nauticalObject, value);

    if (message !== "") {
        return message;
    }

    // value should only have digits, except if object has no light, then last character can be M
    const regex = objectHasLight || isRacon ? /^\d{6}$/ : /^\d{5}M$/;
    if (!regex.test(value)) {
        return "Sjømerke nummer har ugyldig format";
    }

    if (!objectHasLight && !isRacon) {
        if (!value.endsWith("M")) {
            return "Sjømerke nummer for objekter uten lys må slutte på M";
        }
    } else if (!/\d$/.test(value)) {
        // if the value ends with anything that is not a digit
        return "Sjømerke nummer for objekter med lys eller Racon må slutte på et tall";
    }

    if (!Object.hasOwn(nauticalObject.attributes, "klasse")) {
        return "Sjømerke klasse mangler";
    }

    switch (nauticalObject.attributes.klasse) {
        case NauticalClasses.FL_Fyrlykt:
        case NauticalClasses.FS_Fyrstasjon:
            if (!value.endsWith("00")) {
                return "Fyrlykt og fyrstasjon sjømerke nummer må være hele 100-tall";
            }
            break;
        case NauticalClasses.IB_IndirekteBelysning:
            if (!value.endsWith("0")) {
                return "HIB sjømerke nummer må være hele 10-tall";
            }
            break;
        case NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning:
        case NauticalClasses.LA_Lanterne:
        case NauticalClasses.LB_Lysboeye:
            // red
            if (nauticalObject.attributes.lys_farge === SeamarkColors.Red) {
                // numberingRule = NumberingRule.Even;
                // if value is not even
                if (
                    value.endsWith("1") ||
                    value.endsWith("3") ||
                    value.endsWith("5") ||
                    value.endsWith("7") ||
                    value.endsWith("9")
                ) {
                    return "Røde lanterne sjømerke nummer må være partall";
                }
            } else if (nauticalObject.attributes.lys_farge === SeamarkColors.Green) {
                // green
                // numberingRule = NumberingRule.Odd;
                // if value is not odd
                if (value.endsWith("2") || value.endsWith("4") || value.endsWith("6") || value.endsWith("8")) {
                    return "Grønne lanterne sjømerke nummer må være oddetall";
                }
            } else if (
                nauticalObject.attributes.lys_farge === SeamarkColors.White ||
                nauticalObject.attributes.lys_farge === SeamarkColors.Yellow
            ) {
                // yellow or white
                if (!value.endsWith("0")) {
                    return "Hvit lanterne sjømerke nummer må være hele 10-tall";
                }
            } else {
                return "Kan validere sjømerkenummer kun for hvitt, rødt eller grønt lys";
            }

            break;
    }

    let existingSeamarkNumbers = await getExistingSeamarkNumbers(nauticalObject, region);

    if (existingSeamarkNumbers.includes(value)) {
        const numberOfExisting = existingSeamarkNumbers.filter((num) => num === value).length;
        return (
            "Sjømerke nummeret er allerede i bruk (" +
            numberOfExisting +
            (numberOfExisting > 1 ? " andre forekomster" : " annen forekomst") +
            ")"
        );
    }

    return "";
};

export const getValidSeamarkRange = (
    nauticalObject: Graphic,
    value?: string
): { lowValue: string; highValue: string; message: string } => {
    const objectHasLight = Object.hasOwn(nauticalObject.attributes, "lys_type");
    const isRacon = (nauticalObject.layer as FeatureLayer).layerId === FeatureClasses.Racon;

    const region = nauticalObject.attributes.kystverk_region;
    let lowValue = "";
    let highValue = "";
    let message = "";

    switch (region) {
        case KYVRegions.KystverketSørøst:
            if (!objectHasLight && !isRacon) {
                lowValue = "00010M";
                highValue = "09129M";
            } else {
                lowValue = "000100";
                highValue = "090900";
            }
            if (value) {
                if (value < lowValue || value > highValue) {
                    message = `Sjømerkenummer er utenfor Kystverket Sørøst region ${lowValue} - ${highValue}`;
                }
            }
            break;
        case KYVRegions.KystverketVest:
            if (!objectHasLight && !isRacon) {
                lowValue = "09130M";
                highValue = "29999M";
            } else {
                lowValue = "091300";
                highValue = "297000";
            }
            if (value) {
                if (value < lowValue || value > highValue) {
                    message = `Sjømerkenummer er utenfor Kystverket Vest region ${lowValue} - ${highValue}`;
                }
            }
            break;
        case KYVRegions.KystverketMidtNorge:
            if (!objectHasLight && !isRacon) {
                lowValue = "30000M";
                highValue = "56799M";
            } else {
                lowValue = "300000";
                highValue = "567100";
            }
            if (value) {
                if (value < lowValue || value > highValue) {
                    message = `Sjømerkenummer er utenfor Kystverket Midt-Norge region ${lowValue} - ${highValue}`;
                }
            }
            break;
        case KYVRegions.KystverketNordland:
            if (!objectHasLight && !isRacon) {
                lowValue = "56800M";
                highValue = "83509M";
            } else {
                lowValue = "568000";
                highValue = "834700";
            }
            if (value) {
                if (value < lowValue || value > highValue) {
                    message = `Sjømerkenummer er utenfor Kystverket Nordland region ${lowValue} - ${highValue}`;
                }
            }
            break;
        case KYVRegions.KystverketTromsOgFinnmark:
            if (!objectHasLight && !isRacon) {
                lowValue = "83510M";
                highValue = "99500M";
            } else {
                lowValue = "835100";
                highValue = "985100";
            }
            if (value) {
                if (value < lowValue || value > highValue) {
                    message = `Sjømerkenummer er utenfor Kystverket Troms og Finnmark region ${lowValue} - ${highValue}`;
                }
            }
            break;
        case KYVRegions.KystverketSvalbard:
            if (!objectHasLight && !isRacon) {
                lowValue = "99600M";
                highValue = "99999M";
            } else {
                lowValue = "986000";
                highValue = "999999";
            }
            if (value) {
                if (value < lowValue || value > highValue) {
                    message = `Sjømerkenummer er utenfor Kystverket Svalbard region ${lowValue} - ${highValue}`;
                }
            }
            break;
        case KYVRegions.Kontinentalsokkel:
            message = "Objektets region mangler regler"; // Not enough information to generate a seamark number
            break;
        case KYVRegions.Udefinert:
        default:
            message = "Objektets region mangler"; // Not enough information to generate a seamark number
            break;
    }

    return { lowValue, highValue, message };
};

export const getKystverketRegion = async (nauticalObject: Graphic): Promise<number> => {
    let region = 0;

    const graphicGeometry = nauticalObject.geometry;
    if (!graphicGeometry) {
        return region;
    }

    const regionLayer = new FeatureLayer({
        url: AppConfig.NFSMapFeatureServer + "1",
        outFields: ["*"],
        id: "kystverketRegionLayer",
    });

    const regions = await regionLayer.queryFeatures({
        geometry: graphicGeometry,
        spatialRelationship: "intersects",
        outFields: ["*"],
        where: "1=1",
    });

    if (regions.features.length > 0) {
        const regionFeature = regions.features[0];
        if (regionFeature.attributes.hasOwnProperty("regionkode")) {
            region = regionFeature.attributes.regionkode;
        }
    }

    return region;
};

export const getKystverketRode = async (nauticalObject: Graphic): Promise<string> => {
    let rode = "Udefinert";
    const rodeLayer = new FeatureLayer({
        url: AppConfig.NFSMapFeatureServer + "0",
        outFields: ["*"],
        id: "kystverketRodeLayer",
    });

    const graphicGeometry = nauticalObject.geometry;

    if (!graphicGeometry) {
        return rode;
    }

    const rodeFeatures = await rodeLayer.queryFeatures({
        geometry: graphicGeometry,
        spatialRelationship: "intersects",
        outFields: ["*"],
        where: "1=1",
    });

    if (rodeFeatures.features.length > 0) {
        const rodeFeature = rodeFeatures.features[0];
        if (rodeFeature.attributes.hasOwnProperty("rode")) {
            rode = rodeFeature.attributes.rode;
        }
    }

    return rode;
};

/**
 * Placeholder the region lookup returns when no region polygon intersects the geometry.
 * Stored as-is by older code, so it also shows up on existing features.
 */
const UNDEFINED_REGION = 0;

/**
 * Placeholder the rode lookup returns when no rode polygon intersects the geometry.
 */
const UNDEFINED_RODE = "Udefinert";

const hasRealRegion = (value: unknown): value is number =>
    value !== null && value !== undefined && Number(value) !== UNDEFINED_REGION && !Number.isNaN(Number(value));

const hasRealRode = (value: unknown): value is string =>
    value !== null && value !== undefined && String(value).trim() !== "" && String(value) !== UNDEFINED_RODE;

/**
 * True when a feature's kystverk_region / kystverk_rode has no real value - either unset,
 * or one of the placeholders the lookups return when nothing intersects. Drives both the
 * copy-or-look-up decision and the backfill button in the editor, so the two agree on what
 * "missing" means. Any other field is reported as present; it is none of this helper's
 * business.
 */
export const isKystverketFieldMissing = (field: string, value: unknown): boolean => {
    if (field === "kystverk_region") {
        return !hasRealRegion(value);
    }
    if (field === "kystverk_rode") {
        return !hasRealRode(value);
    }
    return false;
};

/**
 * Resolve kystverk_region and kystverk_rode for a feature.
 *
 * A value is copied from `source` when that object has a real one - a replacement inherits
 * the geometry of the object it replaces, so the region and rode are the same by
 * construction and there is no reason to pay for a lookup. Only what the source is missing
 * is looked up from `feature`'s geometry.
 *
 * Each field resolves independently, and anything that cannot be resolved comes back as
 * null - never as the 0 / "Udefinert" placeholders. That lets callers skip the write and
 * keep whatever the feature already had instead of overwriting it with a non-value.
 *
 * (Params): feature - the feature to resolve values for; its geometry drives any lookup
 * (Params): source - optional object to copy values from before falling back to a lookup
 *
 * Returns a Promise<{ region: number | null; rode: string | null }>
 */
export const resolveKystverketRegionAndRode = async (
    feature: Graphic,
    source?: Graphic | null | undefined
): Promise<{ region: number | null; rode: string | null }> => {
    const sourceRegion = source?.attributes?.kystverk_region;
    const sourceRode = source?.attributes?.kystverk_rode;

    const regionPromise: Promise<number | null> = hasRealRegion(sourceRegion)
        ? Promise.resolve(Number(sourceRegion))
        : getKystverketRegion(feature)
              .then((region) => (hasRealRegion(region) ? region : null))
              .catch((error) => {
                  console.error("Kunne ikke hente Kystverket-region for objektet:", error);
                  return null;
              });

    const rodePromise: Promise<string | null> = hasRealRode(sourceRode)
        ? Promise.resolve(String(sourceRode))
        : getKystverketRode(feature)
              .then((rode) => (hasRealRode(rode) ? rode : null))
              .catch((error) => {
                  console.error("Kunne ikke hente Kystverket-rode for objektet:", error);
                  return null;
              });

    const [region, rode] = await Promise.all([regionPromise, rodePromise]);

    return { region, rode };
};

/**
 * Sort sea map numbers ascending.
 *
 * Values that parse as numbers are sorted numerically and come first.
 * Any value that is not a valid number is kept and sorted alphabetically after the numeric ones,
 * so no sea map number is ever lost because of unexpected content.
 *
 * (Params): seaMapNumbers - The sea map numbers to sort
 *
 * Returns string[] - A new, sorted array of sea map numbers
 */
export const sortSeaMapNumbers = (seaMapNumbers: string[]): string[] => {
    // Values are typed as string, but kartnummer is nullable in the source data,
    // so guard against null/undefined slipping through at runtime.
    const toText = (value: string): string => (value === null || value === undefined ? "" : String(value));

    const toNumber = (value: string): number | null => {
        const trimmed = toText(value).trim();
        if (trimmed === "") {
            return null;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    };

    return [...seaMapNumbers].sort((a, b) => {
        const numberA = toNumber(a);
        const numberB = toNumber(b);

        if (numberA !== null && numberB !== null) {
            return numberA - numberB;
        }
        if (numberA !== null) {
            return -1; // Numeric values before text values
        }
        if (numberB !== null) {
            return 1;
        }
        return toText(a).localeCompare(toText(b), "nb");
    });
};

/**
 * Get Sea map number from HovedkartIndex and HovedKartIndexCL layers
 * Returns a string of all unique sea map numbers
 * 
 * (Params): Point - The point to query the layers with
 * 
 * Returns a Promise<string> - A promise that resolves to a string of unique sea map numbers    
 */
export const getSeaMapNumber = async (point: Point): Promise<string> => {
    if (!point) {
        return "";
    }

    const seaMapLayers = [new FeatureLayer({
        url: AppConfig.NFSMapFeatureServer + "2",
        outFields: ["*"],
        id: "seaMapLayer1",
    }), new FeatureLayer({
        url: AppConfig.NFSMapFeatureServer + "3",
        outFields: ["*"],
        id: "seaMapLayer2",
    })];

    const queryResults = await Promise.all(
        seaMapLayers.map(async (seaMapLayer) => {
            try {
                return await seaMapLayer.queryFeatures({
                    geometry: point,
                    spatialRelationship: "intersects",
                    outFields: ["*"],
                    where: "1=1",
                });
            } catch (error) {
                console.error(`Failed to query sea map layer ${seaMapLayer.id}`, error);
                return null;
            }
        })
    );

    const uniqueSeaMapNumbers = new Set<string>();

    for (const queryResult of queryResults) {
        for (const feature of queryResult?.features ?? []) {
            const seaMapNumber = feature.attributes?.kartnummer;
            if (seaMapNumber !== null && seaMapNumber !== undefined && String(seaMapNumber).trim() !== "") {
                uniqueSeaMapNumbers.add(String(seaMapNumber).trim());
            }
        }
    }

    return sortSeaMapNumbers([...uniqueSeaMapNumbers]).join(", ");
}